// components/FeaturesSection.jsx
'use client'

import Image from 'next/image'
import { useState, useEffect } from 'react'
import useInView from '@/Components/hook/useInView'
import clsx from 'clsx'

const cardsData = [
    {
        title: 'Ask Once, Get Multiple Perspectives',
        description:
            'Draft your query once and get simultaneous answers from GPT-4, Claude, Gemini and more. Compare answers side-by-side to find the best response.',
        images: [
            {
                src: '/Integra-ai/w2.png',
                className: 'absolute left-2 md:left-6 bottom-6 rounded-lg shadow-md',
                width: 458,
                height: 192,
            },
            {
                src: '/Integra-ai/w1.png',
                className: 'absolute right-2 md:right-6 top-6 rounded-lg shadow-xl',
                width: 358,
                height: 203,
            },
        ],
    },
    {
        title: 'Seamless Model Switching',
        description:
            'Dissatisfied with a response? Instantly switch between AI models with a single click without losing context or history. Transfer your entire conversation seamlessly.',
        images: [
            {
                src: '/Integra-ai/w5.png',
                className: 'absolute bottom-0 right-0  rounded-lg shadow-md',
                width: 400,
                height: 85,
            },
            {
                src: '/Integra-ai/w3.png',
                className: 'absolute left-2 md:left-0 top-6 rounded-lg shadow-lg',
                width: 138,
                height: 301,
            },
            {
                src: '/Integra-ai/w4.png',
                className: 'absolute right-4 md:left-32 top-10 rounded-lg shadow-lg',
                width: 299,
                height: 295,
            },
        ],
    },
    {
        title: 'Enterprise Management & Cost Controls',
        description:
            'Take control of AI spending with budget alerts, token spend limitations and detailed usage analytics. Assign API keys and budgets by department for precise resource control.',
        images: [
            {
                src: '/Integra-ai/w6.png',
                className: 'absolute top-6 left-2 md:left-6 rounded-lg shadow-lg',
                width: 339,
                height: 271,
            },
            {
                src: '/Integra-ai/w7.png',
                className: 'absolute right-2 md:right-6 top-20 rounded-lg shadow-md',
                width: 374,
                height: 221,
            },
            {
                src: '/Integra-ai/w8.png',
                className: 'absolute right-4 md:right-0 bottom-0 rounded-lg shadow-md',
                width: 217,
                height: 165,
            },
        ],
    },
    {
        title: 'Bring Your Own Keys (BYOK) Model',
        description:
            'Maintain direct billing relationships with AI vendors and avoid markups or hidden fees from Integra AI.',
        images: [
            {
                src: '/Integra-ai/w9.png',
                className: 'absolute right-4 md:right-10 top-10 rounded-lg shadow-lg',
                width: 299,
                height: 295,
            },
            {
                src: '/Integra-ai/w10.png',
                className: 'absolute left-2 md:left-6 top-6 rounded-lg shadow-md',
                width: 290,
                height: 309,
            },
        ],
    },
]

const CardItem = ({ card, index, setActiveIndex }) => {
    const [ref, isVisible] = useInView({ threshold: 0.5 })

    useEffect(() => {
        if (isVisible) setActiveIndex(index)
    }, [isVisible, index, setActiveIndex])

    return (
        <div
            ref={ref}
            className={clsx(
                'mb-8 min-h-[250px] w-full items-center gap-6 transition-all duration-700 sm:w-[90%] md:mb-20 md:min-h-[400px] md:gap-10 lg:w-[600px]',
                {
                    'translate-y-10 opacity-0': !isVisible,
                    'translate-y-0 opacity-100': isVisible,
                },
            )}>
            <div className={index % 2 === 1 ? 'order-2 lg:order-1' : ''}>
                <h3 className='text-2xl font-bold leading-10 text-gray-900 md:text-3xl lg:text-4xl'>
                    {card.title}
                </h3>
                <p className='mt-3 text-base text-gray-600 md:mt-6 md:text-lg'>
                    {card.description}
                </p>
            </div>
        </div>
    )
}

export default function FeaturesSection() {
    const [activeIndex, setActiveIndex] = useState(0)
    const [fadeKey, setFadeKey] = useState(0)

    useEffect(() => {
        setFadeKey((prev) => prev + 1)
    }, [activeIndex])

    return (
        <section className='relative bg-white '>
            <h2 className='bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-center text-3xl font-bold leading-snug text-transparent md:text-4xl lg:text-5xl'>
                Unprecedented Intelligence,
                <br />
                Unmatched Control
            </h2>

            <div className='mx-auto mt-12 flex w-full flex-col gap-6 px-4 sm:px-8 md:mt-28 md:gap-4 md:px-12 lg:flex-row lg:px-20'>
                {/* Text Column */}
                <div className='h-full flex-1 gap-8 px-2 sm:px-6 md:gap-16 md:px-10'>
                    {cardsData.map((card, index) => (
                        <CardItem
                            key={index}
                            card={card}
                            index={index}
                            setActiveIndex={setActiveIndex}
                        />
                    ))}
                </div>

                {/* Sticky Image Column */}
                <div className='sticky top-40 hidden h-[360px] flex-1 md:top-60 md:flex'>
                    <div className='relative mx-auto hidden xl:block h-full w-full max-w-[600px] rounded-2xl border-[2px] border-[#EAB3081A] bg-gradient-to-r from-orange-500/[0.102] to-amber-500/[0.102]'>
                        <div className='left-[923px] -top-[152px]  h-[347px] w-[447px] -scale-x-100 rounded-full border border-[#eab2085e]' />

                        {cardsData[activeIndex].images.map((img, i) => (
    <Image
        key={`${fadeKey}-${i}`}
        src={img.src}
        alt={`Feature ${activeIndex + 1} Image ${i + 1}`}
        width={img.width}
        height={img.height}        
        className={clsx(
            'absolute transition-opacity duration-700',
            img.className,
            'glow-shadow' // custom glow
        )}
    />
))}

                    </div>
                </div>
            </div>
        </section>
    )
}
